

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const OrderCancellationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [cancellation, setCancellation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchCancellationData();
    }, [id]);

    const fetchCancellationData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/order-cancellations/${id}`);
            setCancellation(response.data);
        } catch (error) {
            console.error('Error fetching order cancellation data:', error);
            setSnackbar({ open: true, message: 'Error loading order cancellation details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setCancellation(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/order-cancellations/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Order Cancellation updated successfully', severity: 'success' });
            fetchCancellationData();
        } catch (error) {
            console.error('Error updating order cancellation:', error);
            setSnackbar({ open: true, message: 'Error updating order cancellation', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this order cancellation?')) {
            try {
                await axiosInstance.delete(`/order-cancellations/${id}`);
                setSnackbar({ open: true, message: 'Order Cancellation deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/order-cancellations'), 1000);
            } catch (error) {
                console.error('Error deleting order cancellation:', error);
                setSnackbar({ open: true, message: 'Error deleting order cancellation', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={cancellation?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!cancellation) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Order Cancellation not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {cancellation.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Order Cancellation Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchCancellationData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Order Cancellation">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Order Cancellation">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Cancellation Information", [
                                    { label: "Cancellation Name", field: "name" },
                                    { label: "Travel Order", field: "travel_order_id", readOnly: true, renderValue: (val) => cancellation.travel_order?.name || val || '-' },
                                    { label: "Invoice", field: "invoice_id", readOnly: true, renderValue: (val) => cancellation.invoice?.invoice_number || cancellation.invoice?.name || val || '-' },
                                    { label: "Payment Transaction", field: "payment_transaction_id", readOnly: true, renderValue: (val) => cancellation.payment_transaction?.name || val || '-' },
                                    { label: "Travel Invoice", field: "travel_invoice_id", readOnly: true, renderValue: (val) => cancellation.travel_invoice?.name || cancellation.travel_invoice?.invoice_number || val || '-' },
                                    { label: "Order Line Item", field: "order_line_item_id", readOnly: true, renderValue: (val) => cancellation.order_line_item?.name || val || '-' },
                                    { label: "Approval Status", field: "approval_status", options: [
                                        { value: "Not Submitted", label: "Not Submitted" },
                                        { value: "Pending Approval", label: "Pending Approval" },
                                        { value: "Approved-Not Processed", label: "Approved-Not Processed" },
                                        { value: "Processed", label: "Processed" },
                                        { value: "Rejected", label: "Rejected" }
                                    ]},
                                    { label: "Status", field: "status", options: [
                                        { value: "Not Submitted", label: "Not Submitted" },
                                        { value: "Submitted", label: "Submitted" },
                                        { value: "Approved", label: "Approved" },
                                        { value: "Rejected", label: "Rejected" }
                                    ]},
                                    { label: "Cancellation Type", field: "cancellation_type", options: [
                                        { value: "Departure Date Change", label: "Departure Date Change" },
                                        { value: "Min. Passenger Not Met", label: "Min. Passenger Not Met" },
                                        { value: "Customer Cancellation", label: "Customer Cancellation" },
                                        { value: "Non Payment", label: "Non Payment" },
                                        { value: "Circumstantial", label: "Circumstantial" }
                                    ]},
                                    { label: "Requested On", field: "requested_on", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Amount Deduction", field: "amount_deduction", renderValue: (val) => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                    { label: "Total Amount", field: "total_amount", renderValue: (val) => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                    { label: "Refund Amount", field: "refund_amount", renderValue: (val) => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                ])}

                                {renderSection("Additional Information", [
                                    { label: "Reject Reason", field: "reject_reason" },
                                    { label: "Comments", field: "comments" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default OrderCancellationDetail;

